import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { useToast } from "@/components/ux/Toast";
import { useT } from "@/lib/i18n";
import { useCreateClubPost } from "@/lib/api";
import { composeBody, MAX_POST_LENGTH, usePostAttachments, type PostAttachments } from "./usePostAttachments";

/** Attached photo thumbnails (with remove buttons) plus an upload spinner. */
export function AttachmentStrip({ attachments }: { attachments: PostAttachments }) {
  const { t } = useT();
  if (attachments.images.length === 0 && !attachments.uploading) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {attachments.images.map((url) => (
        <div key={url} className="relative">
          <img src={url} alt="" className="w-16 h-16 rounded-lg object-cover" />
          <button
            type="button"
            onClick={() => attachments.remove(url)}
            className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-black/70 text-white"
            aria-label={t("clubs.removeImage")}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      {attachments.uploading && (
        <div className="w-16 h-16 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />
        </div>
      )}
    </div>
  );
}

/** "Photo" button with its hidden file input. */
export function AddPhotoButton({ attachments }: { attachments: PostAttachments }) {
  const { t } = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={!attachments.canAdd}
        className="btn btn-ghost btn-sm gap-1.5"
      >
        <ImagePlus className="w-4 h-4" /> {t("clubs.addPhoto")}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) attachments.add(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );
}

/** Character counter shown near the limit. */
export function LengthCounter({ length }: { length: number }) {
  if (length <= MAX_POST_LENGTH * 0.9) return null;
  return (
    <span className={`text-xs ${length > MAX_POST_LENGTH ? "text-red-500 font-medium" : "text-neutral-500"}`}>
      {length}/{MAX_POST_LENGTH}
    </span>
  );
}

/**
 * Inline reply box at the bottom of a thread. New threads are written on the
 * ClubNewPostSheet instead.
 */
export function ClubComposer({ slug, parentId }: { slug: string; parentId: string }) {
  const { t } = useT();
  const toast = useToast();
  const createPost = useCreateClubPost(slug);
  const attachments = usePostAttachments();
  const [text, setText] = useState("");

  const body = composeBody(text, attachments.images);
  const canPost = !!body && body.length <= MAX_POST_LENGTH && !createPost.isPending && !attachments.uploading;

  const submit = () => {
    if (!canPost) return;
    createPost.mutate(
      { body, parentId },
      {
        onSuccess: () => {
          setText("");
          attachments.reset();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="card space-y-3"
    >
      <MarkdownEditor value={text} onChange={setText} onSubmit={submit} compact placeholder={t("clubs.replyPlaceholder")} />
      <AttachmentStrip attachments={attachments} />
      <div className="flex items-center gap-2">
        <AddPhotoButton attachments={attachments} />
        <span className="ml-auto flex items-center gap-3">
          <LengthCounter length={body.length} />
          <button type="submit" disabled={!canPost} className="btn btn-primary btn-sm">
            {createPost.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("clubs.reply")}
          </button>
        </span>
      </div>
    </form>
  );
}
